import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { TransactionDisputeDto } from "@/features/wallet/api/dto";
import { useRejectTransactionDispute } from "@/features/wallet/api/use-reject-transaction-dispute";
import { createRejectTransactionDisputeSchema } from "@/features/wallet/schemas/dispute.schema";

/**
 * Rejet d'une contestation ouverte (`POST /TransactionDisputes/{id}/reject`,
 * guide v3.1) — flashpay-superadmin / flashpay-admin. Le gel est levé et le
 * montant redevient disponible pour le bénéficiaire : aucune circulation de
 * fonds, donc pas de maker-checker (décision serveur). Le bénéficiaire est
 * prévenu du déblocage, l'expéditeur de l'issue.
 *
 * Opération sans frais : note obligatoire (5–500 caractères) saisie dans le
 * dialog, puis `MutationConfirmPreview` avant l'appel API idempotent.
 */
export function RejectTransactionDisputeDialog({
  dispute,
  open,
  onOpenChange,
}: {
  dispute: TransactionDisputeDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  const tPreview = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit au changement de langue.
  const schema = useMemo(() => createRejectTransactionDisputeSchema(), [i18n.language]);

  const reject = useRejectTransactionDispute(dispute.id);
  const pending = reject.isPending;
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState<string | undefined>();
  const [preview, setPreview] = useState<string | null>(null);

  const closeAll = () => {
    if (pending) return;
    setNote("");
    setNoteError(undefined);
    setPreview(null);
    onOpenChange(false);
  };

  const handleSubmit = () => {
    const parsed = schema.safeParse({ note });
    if (!parsed.success) {
      setNoteError(parsed.error.issues[0]?.message);
      return;
    }
    setPreview(parsed.data.note);
  };

  return (
    <>
      <Dialog
        open={open && preview === null}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeAll();
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("wallet.disputes.reject.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("wallet.disputes.reject.description")}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="dispute-reject-note">
              {t("wallet.disputes.reject.note")}
            </Label>
            <Textarea
              id="dispute-reject-note"
              maxLength={500}
              placeholder={t("wallet.disputes.reject.notePlaceholder")}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                if (noteError) setNoteError(undefined);
              }}
            />
            {noteError ? (
              <p className="text-sm text-destructive">{noteError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeAll}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={pending}>
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={
          preview !== null
            ? {
                disputeId: dispute.id,
                transactionReference: dispute.transactionReference,
                note: preview,
              }
            : {}
        }
        title={t("wallet.disputes.reject.title")}
        description={t("common.mutationPreview.description")}
        t={tPreview}
        confirmLabel={t("wallet.disputes.resolution.reject")}
        pending={pending}
        onConfirm={() => {
          if (preview === null) return;
          reject.mutate(
            { note: preview },
            {
              onSuccess: () => {
                notifSilence(t("wallet.disputes.feedback.rejected"));
                setNote("");
                setNoteError(undefined);
                setPreview(null);
                onOpenChange(false);
              },
              onError: (error) =>
                notifSilenceError(apiErrorHandler(error) || error.message),
            }
          );
        }}
      />
    </>
  );
}
